import { create } from 'zustand';

export type NavigableError = {
  id: string;
  type: 'required' | string;
};
/**
 * Error service class for managing errors in a web application.
 * This class provides methods to navigate through errors, clear them,
 * and manage the current error state.
 */
export abstract class ErrorsNavigationService<T extends NavigableError> {
  protected store: ReturnType<typeof createErrorsStore<T>>;
  protected virtualizedScrollCallbacks = new Map<string, (index: number) => void>();

  abstract focusOnCurrentError(): void;

  constructor() {
    this.store = createErrorsStore<T>();
  }

  registerVirtualizedScroll = (tableId: string, scrollToIndex: (index: number) => void): void => {
    this.virtualizedScrollCallbacks.set(tableId, scrollToIndex);
  };

  unregisterVirtualizedScroll = (tableId: string): void => {
    this.virtualizedScrollCallbacks.delete(tableId);
  };

  scrollToVirtualizedRow = (tableId: string, rowIndex: number): void => {
    const scrollToIndex = this.virtualizedScrollCallbacks.get(tableId);
    if (scrollToIndex) {
      scrollToIndex(rowIndex);
    }
  };

  getCurrentError = (): T | undefined => {
    return this.store.getState().errors[this.store.getState().currentErrorIndex];
  };

  getNextError = (): T | undefined => {
    const state = this.store.getState();
    if (state.nextErrorIndex < 0) {
      return undefined;
    }
    return state.errors[state.nextErrorIndex];
  };

  getPreviousError = (): T | undefined => {
    const state = this.store.getState();
    if (state.previousErrorIndex < 0) {
      return undefined;
    }
    return state.errors[state.previousErrorIndex];
  };

  getFirstNonPendingError = (): T | undefined => {
    const state = this.store.getState();
    const { errors, currentErrorIndex, pendingFieldIds } = state;

    if (currentErrorIndex >= 0 && !pendingFieldIds.has(errors[currentErrorIndex]?.id)) {
      return errors[currentErrorIndex];
    }

    const index = findNextNonPendingIndex(currentErrorIndex, errors, pendingFieldIds);
    if (index >= 0) {
      return errors[index];
    }

    return errors[0];
  };

  resetFocus = (): void => {
    const firstNonPendingError = this.getFirstNonPendingError();
    if (firstNonPendingError) {
      const index = this.store.getState().errors.findIndex((e: T) => e.id === firstNonPendingError.id);
      if (index !== -1) {
        this.store.getState().setCurrentError(index);
      }
    }
    this.focusOnCurrentError();
  };

  moveToPreviousError = (): void => {
    if (this.store.getState().previousErrorIndex >= 0) {
      this.store.getState().setCurrentError(this.store.getState().previousErrorIndex);
    }
    this.focusOnCurrentError();
  };

  moveToNextError = (): void => {
    if (this.store.getState().nextErrorIndex >= 0) {
      this.store.getState().setCurrentError(this.store.getState().nextErrorIndex);
    }
    this.focusOnCurrentError();
  };

  clearErrors = (): void => {
    this.store.getState().clearErrors();
  };

  /**
   * Hook to subscribe to all errors
   */
  useCurrentError = () => {
    return this.store((state) => (state.currentErrorIndex >= 0 ? state.errors[state.currentErrorIndex] : undefined));
  };

  /**
   * Hook to subscribe to all errors
   */
  useErrors = () => {
    return this.store((state) => state.errors);
  };

  /**
   * Hook to subscribe to the number of active (non-pending) errors
   */
  useNumberOfActiveErrors = () => {
    return this.store((state) => state.numberOfErrors - state.pendingErrorsCount);
  };

  /**
   * Hook to subscribe to a specific error by id
   */
  useErrorById = (id: string) => {
    return this.store((state) => state.errorsMap[id]);
  };

  getErrorById = (id: string | undefined): T | undefined => {
    return id ? this.store.getState().errorsMap[id] : undefined;
  };
  setCurrentError = (error: T): number => {
    const state = this.store.getState();
    const index = state.errors.findIndex((e: T) => e.id === error.id);
    if (index !== -1) {
      state.setCurrentError(index);
      this.focusOnCurrentError();
    }
    return index;
  };

  setErrors = (errors: Array<T>): void => {
    this.store.getState().setErrors(errors);
  };
}

/**
 * Store state type including actions.
 */
type ErrorsNavigationState<T extends NavigableError> = {
  // State
  errors: Array<T>;
  errorsMap: Record<string, T>;
  currentErrorIndex: number;
  nextErrorIndex: number;
  previousErrorIndex: number;
  numberOfErrors: number;
  pendingFieldIds: Set<string>;
  pendingErrorsCount: number;

  // Actions
  setCurrentError: (errorIndex: number) => void;
  clearErrors: () => void;
  setErrors: (errors: Array<T>) => void;
  setFieldsPendingValidation: (fieldIds: Array<string>) => void;
  clearFieldsPendingValidation: () => void;
};

const findNextNonPendingIndex = <T extends NavigableError>(
  currentIndex: number,
  errors: Array<T>,
  pendingFieldIds: Set<string>,
): number => {
  if (errors.length === 0) {
    return -1;
  }

  let nextIndex = (currentIndex + 1) % errors.length;
  let checked = 0;

  while (checked < errors.length) {
    if (!pendingFieldIds.has(errors[nextIndex].id)) {
      return nextIndex;
    }
    nextIndex = (nextIndex + 1) % errors.length;
    checked++;
  }

  return -1;
};

const findPreviousNonPendingIndex = <T extends NavigableError>(
  currentIndex: number,
  errors: Array<T>,
  pendingFieldIds: Set<string>,
): number => {
  if (errors.length === 0) {
    return -1;
  }

  const length = errors.length;
  let prevIndex = (currentIndex - 1 + length) % length;
  let checked = 0;

  while (checked < errors.length) {
    if (!pendingFieldIds.has(errors[prevIndex].id)) {
      return prevIndex;
    }
    prevIndex = (prevIndex - 1 + length) % length;
    checked++;
  }

  return -1;
};

/**
 * Creates a Zustand store for error management.
 */
function createErrorsStore<T extends NavigableError>() {
  return create<ErrorsNavigationState<T>>()((set) => ({
    // Initial state
    errors: [],
    errorsMap: {},
    currentErrorIndex: -1,
    nextErrorIndex: -1,
    previousErrorIndex: -1,
    numberOfErrors: 0,
    pendingFieldIds: new Set(),
    pendingErrorsCount: 0,

    // Actions
    setErrors: (errors: Array<T>) => {
      set((state) => {
        const errorsMap = errors.reduce(
          (map, error) => {
            map[error.id] = error;
            return map;
          },
          {} as Record<string, T>,
        );

        const numberOfErrors = errors.length;
        const { pendingFieldIds } = state;
        let currentErrorIndex = state.currentErrorIndex;
        let nextErrorIndex = -1;
        let previousErrorIndex = -1;

        if (errors.length > 0) {
          if (currentErrorIndex === -1) {
            currentErrorIndex = 0;
          } else if (currentErrorIndex >= errors.length) {
            currentErrorIndex = 0;
          }

          nextErrorIndex = findNextNonPendingIndex(currentErrorIndex, errors, pendingFieldIds);
          previousErrorIndex = findPreviousNonPendingIndex(currentErrorIndex, errors, pendingFieldIds);
        } else {
          currentErrorIndex = -1;
        }

        return {
          ...state,
          errors,
          errorsMap,
          numberOfErrors,
          currentErrorIndex,
          nextErrorIndex,
          previousErrorIndex,
        };
      });
    },

    setCurrentError: (errorIndex: number) => {
      set((state) => {
        const { errors, pendingFieldIds } = state;
        const errorsLength = errors.length;

        if (errorsLength === 0) {
          return state;
        }

        let currentErrorIndex = errorIndex;
        if (currentErrorIndex < 0) {
          currentErrorIndex = errorsLength - 1;
        } else if (currentErrorIndex >= errorsLength) {
          currentErrorIndex = 0;
        }

        const nextErrorIndex = findNextNonPendingIndex(currentErrorIndex, errors, pendingFieldIds);
        const previousErrorIndex = findPreviousNonPendingIndex(currentErrorIndex, errors, pendingFieldIds);

        return {
          ...state,
          currentErrorIndex,
          nextErrorIndex,
          previousErrorIndex,
        };
      });
    },

    clearErrors: () => {
      set({
        errors: [],
        errorsMap: {},
        currentErrorIndex: -1,
        nextErrorIndex: -1,
        previousErrorIndex: -1,
        numberOfErrors: 0,
        pendingFieldIds: new Set(),
        pendingErrorsCount: 0,
      });
    },

    setFieldsPendingValidation: (fieldIds: Array<string>) => {
      set((state) => {
        const pendingFieldIds = new Set(fieldIds);
        const { errors, currentErrorIndex } = state;

        const nextErrorIndex = findNextNonPendingIndex(currentErrorIndex, errors, pendingFieldIds);
        const previousErrorIndex = findPreviousNonPendingIndex(currentErrorIndex, errors, pendingFieldIds);

        return {
          ...state,
          pendingFieldIds,
          pendingErrorsCount: fieldIds.length,
          nextErrorIndex,
          previousErrorIndex,
        };
      });
    },

    clearFieldsPendingValidation: () => {
      set((state) => ({
        ...state,
        pendingFieldIds: new Set(),
        pendingErrorsCount: 0,
        nextErrorIndex: state.errors.length > 0 ? 0 : -1,
        previousErrorIndex: state.errors.length > 0 ? state.errors.length - 1 : -1,
      }));
    },
  }));
}
